import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from '../api';

const TYPE_LABELS = {
  ERROR:       { label: 'Genel Hata',      color: '#ef4444', bg: '#fef2f2', icon: '🔴' },
  FETCH_ERROR: { label: 'Ağ / API Hatası', color: '#f97316', bg: '#fff7ed', icon: '📡' },
  CORS_ERROR:  { label: 'CORS Hatası',     color: '#a855f7', bg: '#faf5ff', icon: '🚫' },
  AUTH_FAIL:   { label: 'Giriş Hatası',    color: '#eab308', bg: '#fefce8', icon: '🔐' },
  EXPLOIT:     { label: 'Şüpheli İşlem',   color: '#dc2626', bg: '#fef2f2', icon: '⚠️' },
  CLICK:       { label: 'Tıklama',         color: '#6b7280', bg: '#f9fafb', icon: '🖱️' },
  PAGE_VIEW:   { label: 'Sayfa Görüntüleme', color: '#3b82f6', bg: '#eff6ff', icon: '👁️' },
};

const FILTERS = [
  { value: '',            label: 'Tümü' },
  { value: 'FETCH_ERROR', label: 'Ağ Hatası' },
  { value: 'CORS_ERROR',  label: 'CORS' },
  { value: 'ERROR',       label: 'Hata' },
  { value: 'AUTH_FAIL',   label: 'Giriş Hatası' },
  { value: 'EXPLOIT',     label: 'Şüpheli' },
];

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// Metadayı anlaşılır Türkçe açıklamalara dönüştüren yardımcı fonksiyon
const getHumanReadableDescription = (log) => {
  const meta = log.metadata || '';
  const type = log.type || '';
  const path = log.path || '';
  const target = log.target || '';

  let title = 'Sistem Bildirimi';
  let explanation = 'Sistemde bir işlem kaydedildi.';
  let categoryIcon = 'ℹ️';
  let badgeColor = '#6366f1';
  let badgeBg = '#eef2ff';

  if (type === 'FETCH_ERROR') {
    categoryIcon = '📡';
    badgeColor = '#d97706';
    badgeBg = '#fffbeb';
    title = 'Ağ / Sunucu İletişim Hatası';
    explanation = `Kullanıcı "${path || 'Bilinmeyen sayfa'}" sayfasındayken "${target || 'API'}" servisine bağlanamadı. İnternet bağlantı sorunu, sunucu çökmesi veya zaman aşımı yaşanmış olabilir.`;
  } else if (type === 'CORS_ERROR') {
    categoryIcon = '🚫';
    badgeColor = '#9333ea';
    badgeBg = '#faf5ff';
    title = 'CORS Güvenlik Yetki Engeli';
    explanation = `Tarayıcı, "${target || 'API'}" adresinden gelen yanıtı güvenlik kuralları (CORS) nedeniyle engelledi. Sunucu izin başlıkları kontrol edilmelidir.`;
  } else if (type === 'AUTH_FAIL') {
    categoryIcon = '🔐';
    badgeColor = '#ca8a04';
    badgeBg = '#fefce8';
    title = 'Başarısız Giriş Denemesi';
    explanation = `Bir kullanıcı veya bot sisteme yanlış şifre veya kullanıcı adı ile giriş yapmaya çalıştı.`;
  } else if (type === 'EXPLOIT') {
    categoryIcon = '⚠️';
    badgeColor = '#dc2626';
    badgeBg = '#fef2f2';
    title = 'Şüpheli İşlem / Güvenlik Saldırı Uyarısı';
    explanation = `İstekte zararlı kod kalıbı (SQL Injection, XSS veya Path Traversal) tespit edildi. Güvenlik duvarı işlemi otomatik engelledi.`;
  } else if (type === 'ERROR') {
    categoryIcon = '🔴';
    badgeColor = '#dc2626';
    badgeBg = '#fef2f2';
    title = 'Arayüz / Yazılım Çökme Hatası';
    explanation = `Kullanıcı "${path || 'Bilinmeyen sayfa'}" sayfasındayken bir JavaScript hatası oluştu ve sayfanın bir kısmı yanıt vermedi.`;
  }

  // Temiz hata mesajı çıkarma
  let mainErrorMessage = meta;
  if (meta.includes('Error:')) {
    mainErrorMessage = meta.split('Error:')[1]?.trim() || meta;
  }

  return { title, explanation, categoryIcon, mainErrorMessage, badgeColor, badgeBg };
};

export default function HataLoglari() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [modalLog, setModalLog] = useState(null);
  const [showRawCode, setShowRawCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const LIMIT = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      if (filterType) params.set('type', filterType);
      const data = await callApi(`/log-activity?${params.toString()}`, 'GET');
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Loglar yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  }, [filterType, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / LIMIT);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const humanReadable = modalLog ? getHumanReadableDescription(modalLog) : null;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>🛡️ Hata & Aktivite Logları</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Siteden gelen hatalar ve sistem uyarıları — toplam <strong>{total}</strong> kayıt
          </p>
        </div>
        <button
          onClick={fetchLogs}
          style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => { setFilterType(f.value); setPage(0); }}
            style={{
              padding: '6px 14px', borderRadius: '20px', border: '1px solid',
              borderColor: filterType === f.value ? '#4f46e5' : '#d1d5db',
              background: filterType === f.value ? '#4f46e5' : '#fff',
              color: filterType === f.value ? '#fff' : '#374151',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, transition: 'all .15s'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tablo */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Yükleniyor...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <div style={{ fontSize: '3rem', marginBottom: '8px' }}>✅</div>
          <p style={{ margin: 0 }}>Bu kategoride hiç log bulunmuyor.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {/* Tablo başlığı */}
          <div style={{
            display: 'grid', gridTemplateColumns: '140px 1fr 180px 70px 120px',
            padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
            fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>
            <span>Tür</span><span>Sayfa / Hedef</span><span>Tarih</span><span>Ülke</span><span>İncele</span>
          </div>

          {/* Satırlar */}
          {logs.map(log => {
            const t = TYPE_LABELS[log.type] || TYPE_LABELS.ERROR;
            const isExpanded = expandedId === log.id;
            return (
              <React.Fragment key={log.id}>
                <div
                  style={{
                    display: 'grid', gridTemplateColumns: '140px 1fr 180px 70px 120px',
                    padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
                    alignItems: 'center',
                    background: isExpanded ? '#f9fafb' : '#fff',
                  }}
                >
                  {/* Tür badge */}
                  <div>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 8px', borderRadius: '12px',
                      background: t.bg, color: t.color,
                      fontSize: '0.72rem', fontWeight: 700
                    }}>
                      {t.icon} {t.label}
                    </span>
                  </div>

                  {/* Sayfa / Hedef */}
                  <div style={{ overflow: 'hidden', cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : log.id)}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.path || '-'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.target || ''}
                    </div>
                  </div>

                  {/* Tarih */}
                  <div style={{ fontSize: '0.8rem', color: '#374151' }}>{formatDate(log.tarih)}</div>

                  {/* Ülke */}
                  <div style={{ fontSize: '0.875rem' }}>{log.country || 'TR'}</div>

                  {/* Detaylı İncele Butonu */}
                  <div>
                    <button
                      onClick={() => { setModalLog(log); setShowRawCode(false); }}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '8px',
                        border: '1px solid #6366f1',
                        background: '#eef2ff',
                        color: '#4f46e5',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      🔍 Detaylı Oku
                    </button>
                  </div>
                </div>

                {/* Genişletilmiş hızlı detay */}
                {isExpanded && (
                  <div style={{ padding: '12px 16px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.8rem' }}>
                      {[
                        ['🔑 Log ID', log.id],
                        ['👤 Üye Kodu', log.userCode || 'Giriş yapılmamış (Misafir)'],
                        ['🌍 IP Adresi', log.ip || '-'],
                        ['📱 User Agent', log.userAgent || '-'],
                        ['📋 Hata Mesajı', log.metadata || '-'],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <div style={{ fontWeight: 700, color: '#374151', marginBottom: '2px' }}>{label}</div>
                          <div style={{ color: '#6b7280', wordBreak: 'break-all', fontFamily: label.includes('ID') || label.includes('IP') ? 'monospace' : 'inherit' }}>
                            {value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: page === 0 ? '#f3f4f6' : '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>
            ← Önceki
          </button>
          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Sayfa {page + 1} / {totalPages} &nbsp;·&nbsp; {total} kayıt
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: page >= totalPages - 1 ? '#f3f4f6' : '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>
            Sonraki →
          </button>
        </div>
      )}

      {/* DETAYLI LOG OKUMA MODALI */}
      {modalLog && humanReadable && (
        <div
          onClick={() => setModalLog(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(5px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '20px'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '20px', maxWidth: '680px', width: '100%',
              maxHeight: '90vh', overflowY: 'auto', padding: '28px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>{humanReadable.categoryIcon}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                    {humanReadable.title}
                  </h3>
                  <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    Tarih: {formatDate(modalLog.tarih)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setModalLog(null)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: '50%', width: '32px', height: '32px', fontSize: '1rem', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* 1. KART: Kolay Anlaşılır Türkçe Açıklama */}
            <div style={{ background: '#f8fafc', borderLeft: `4px solid ${humanReadable.badgeColor}`, borderRadius: '12px', padding: '16px 18px', marginBottom: '20px' }}>
              <p style={{ margin: 0, fontSize: '0.92rem', color: '#334155', lineHeight: '1.5', fontWeight: 500 }}>
                {humanReadable.explanation}
              </p>
            </div>

            {/* 2. KART: Ana Hata Mesajı */}
            {humanReadable.mainErrorMessage && (
              <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#9f1239', marginBottom: '4px' }}>
                  ⚠️ Sistem Hata Metni:
                </div>
                <div style={{ fontSize: '0.88rem', color: '#be123c', fontWeight: 600, wordBreak: 'break-word', fontFamily: 'sans-serif' }}>
                  {humanReadable.mainErrorMessage}
                </div>
              </div>
            )}

            {/* 3. KART: Konum ve Kullanıcı Detayları */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                📍 İşlem ve Kullanıcı Detayları
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.83rem' }}>
                <div>
                  <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Sayfa Yolu:</span>
                  <strong style={{ color: '#0f172a', wordBreak: 'break-all' }}>{modalLog.path || '-'}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Hedef / Buton:</span>
                  <strong style={{ color: '#0f172a' }}>{modalLog.target || '-'}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>Kullanıcı / Üye:</span>
                  <strong style={{ color: '#0f172a' }}>{modalLog.userCode || 'Misafir Ziyaretçi'}</strong>
                </div>
                <div>
                  <span style={{ color: '#64748b', display: 'block', marginBottom: '2px' }}>IP & Ülke:</span>
                  <strong style={{ color: '#0f172a' }}>{modalLog.ip || '-'} ({modalLog.country || 'TR'})</strong>
                </div>
              </div>
            </div>

            {/* 4. KART: Geliştirici İçin Ham Teknik Çıktı (Açılır / Kapanır Accordion) */}
            <div style={{ marginBottom: '20px' }}>
              <button
                onClick={() => setShowRawCode(!showRawCode)}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: '10px',
                  border: '1px solid #cbd5e1', background: '#f8fafc',
                  color: '#475569', fontSize: '0.82rem', fontWeight: 600,
                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}
              >
                <span>🛠️ Yazılımcı / Teknik Hata Detayı (Raw Code)</span>
                <span>{showRawCode ? '▲ Gizle' : '▼ Göster'}</span>
              </button>

              {showRawCode && (
                <div style={{ marginTop: '10px', animation: 'fadeIn 0.2s ease' }}>
                  <pre style={{
                    background: '#0f172a', color: '#38bdf8', padding: '14px', borderRadius: '10px',
                    fontSize: '0.78rem', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                    maxHeight: '220px', fontFamily: 'Consolas, monospace', margin: 0
                  }}>
                    {modalLog.metadata || 'Ham metin verisi bulunmuyor.'}
                  </pre>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
                    Cihaz: {modalLog.userAgent || 'Bilinmiyor'}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Alt Butonlar */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              <button
                onClick={() => copyToClipboard(JSON.stringify(modalLog, null, 2))}
                style={{
                  padding: '9px 16px', borderRadius: '10px', border: '1px solid #cbd5e1',
                  background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                  transition: 'all 0.15s'
                }}
              >
                {copied ? '✅ Kopyalandı!' : '📋 JSON Kopyala'}
              </button>
              <button
                onClick={() => setModalLog(null)}
                style={{
                  padding: '9px 24px', borderRadius: '10px', border: 'none',
                  background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
                }}
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
